import React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';

import AudioPlayer from '../components/AudioPlayer';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Notification from '../components/Notification';
import TextContent from '../components/TextContent';
import Section from '../components/Section';
import FooterBar from '../components/FooterBar';
import Grid from '../components/Grid';
import GridItem from '../components/GridItem';
import Table from '../components/Table';

import { GET_ACTIVITY_DETAILS } from '../graph/activities';
import { GET_CMS_CONTENT } from '../graph/cms';
import { GET_BELIEFS, UPDATE_BELIEF } from '../graph/beliefs';

import extractGraphQLError from '../utils/extractGraphQLError';
import {
  extractPrismicString,
  extractPrismicContent,
  hasContent,
} from '../utils/prismic';
import { validate, requiredString } from '../utils/validate';
import useTrackActivityGA from '../utils/useTrackActivityGA';

import { activities, ACTIVITY_STATUS } from '../config/levels';
import { colors } from '../theme/main';

const formValidationSchema = validate({
  title: requiredString,
  description1: requiredString,
});

const formInitialValues = {
  title: '',
  description1: '',
};

const formFields = [
  {
    id: 'title',
    name: 'title',
    label: 'Limiting Belief',
    type: 'textarea',
    placeholder: 'Enter a limiting belief...',
  },
  {
    id: 'description3',
    name: 'description3',
    label: 'Growth Mindset Response',
    type: 'textarea',
    placeholder: 'Growth Mindset Response',
  },
];

function ActivityBeliefStepTwo({ activityId }) {
  const { data: cmsData } = useQuery(GET_CMS_CONTENT, {
    fetchPolicy: 'cache-only',
  });

  const { data, loading } = useQuery(GET_ACTIVITY_DETAILS, {
    variables: { id: activityId },
  });

  const { loading: loadingBeliefs, data: dataBeliefs } = useQuery(GET_BELIEFS, {
    variables: { activityId },
  });

  const filteredBeliefs = dataBeliefs?.beliefs?.filter(
    belief => belief?.type === 'LIMITING',
  );

  const [
    handleUpdateBelief,
    { loading: loadingUpdateBelief, error: errorUpdateBelief },
  ] = useMutation(UPDATE_BELIEF, {
    refetchQueries: ['Beliefs'],
  });

  const activityType = data?.activity?.type;

  useTrackActivityGA(data?.activity?.type, '2');

  const { level } = activities[activityType] || {};

  const {
    title,
    sub_title: subTitle,
    audio_instructions_2: audioInstructions,
    help_text_2: helpText,
    short_instructions_2: shortInstructions,
    step_label_2: stepLabel,
  } =
    cmsData?.cmsContent?.activity?.find(
      activity =>
        activity.id ===
        (cmsData?.user?.currentLevel === 4 &&
        data?.activity?.status === ACTIVITY_STATUS.COMPLETED
          ? `ongoing_${activityType}`
          : activityType),
    ) ||
    cmsData?.cmsContent?.activity?.find(
      activity => activity.id === activityType,
    ) ||
    {};

  return (
    <Layout
      title={`${extractPrismicString(title)}`}
      loading={loading || loadingBeliefs}
      hasFooterBar
    >
      <Container>
        <Section>
          {!!(hasContent(shortInstructions) || hasContent(helpText)) && (
            <Grid>
              <GridItem
                alignTop
                width={
                  audioInstructions?.url || hasContent(helpText) ? 60 : 100
                }
              >
                <TextContent redh6 smallMargin>
                  {extractPrismicContent(shortInstructions)}
                </TextContent>
              </GridItem>

              {!!(audioInstructions?.url || hasContent(helpText)) && (
                <GridItem width={40} alignTop>
                  <Notification showUser noMarginBottom>
                    {extractPrismicContent(helpText)}

                    {!!audioInstructions?.url && (
                      <AudioPlayer fileUrl={audioInstructions?.url} />
                    )}
                  </Notification>
                </GridItem>
              )}
            </Grid>
          )}
        </Section>

        <TextContent redh6 smallMargin>
          <h6>Step 2</h6>
          {extractPrismicContent(stepLabel)}
        </TextContent>

        <Table
          cols={[
            {
              id: 'title',
              value: 'Limiting Belief',
              transformer: value => (
                <span style={{ color: colors.red500 }}>{value}</span>
              ),
            },
            {
              id: 'description3',
              value: 'Growth Mindset Response',
              transformer: value => (
                <span style={{ color: colors.green500 }}>{value}</span>
              ),
            },
          ]}
          data={filteredBeliefs}
          editable
          updateForm={{
            fields: formFields,
            loading: loadingUpdateBelief,
            validationSchema: formValidationSchema,
            initialValues: formInitialValues,
            onSubmit: handleUpdateBelief,
          }}
        />

        {!!errorUpdateBelief && (
          <Notification type="red">
            {extractGraphQLError(errorUpdateBelief)}
          </Notification>
        )}
      </Container>

      <FooterBar
        title={extractPrismicString(title)}
        subTitle={extractPrismicString(subTitle)}
        primaryButton={{
          label: 'Next: Empowering Beliefs',
          to: `/activities/${activityId}/limiting_beliefs/3`,
          level,
        }}
        secondaryButton={{
          label: 'Back to Limiting Beliefs',
          to: `/activities/${activityId}/limiting_beliefs`,
        }}
      />
    </Layout>
  );
}

ActivityBeliefStepTwo.propTypes = {
  activityId: PropTypes.string,
};

ActivityBeliefStepTwo.defaultProps = {
  activityId: null,
};

export default ActivityBeliefStepTwo;
